import React, { Component } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import KpiCard from '../shared/KpiCard';
import { formatNum, CHART_COLORS } from '../shared/formatters';
import constants from '../../../config';

function DisputeSellerRow(props) {
  var s = props.seller;
  return (
    <div className="tw-flex tw-items-center tw-gap-3 tw-py-2 tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222] last:tw-border-0">
      <img
        src={s.profilePic ? (constants.DOMAIN + '/' + s.profilePic.replace(/ /g, '%20')) : '/default-avatar.png'}
        alt=""
        className="tw-w-8 tw-h-8 tw-rounded-full tw-object-cover tw-flex-shrink-0"
      />
      <button
        onClick={function() { props.onOpenUserModal && props.onOpenUserModal(s._id); }}
        className="tw-flex-1 tw-min-w-0 tw-text-left tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-truncate tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 hover:tw-text-[#D4A843]"
      >
        {s.username || 'Unknown'}
      </button>
      <span className="tw-text-sm tw-font-semibold tw-flex-shrink-0" style={{ color: '#EF4444' }}>{s.count} dispute{s.count !== 1 ? 's' : ''}</span>
    </div>
  );
}

class AnalyticsOverview extends Component {
  render() {
    var isDark = document.documentElement.classList.contains('tw-dark');
    var data = this.props.data;
    var self = this;

    if (!data) {
      return <div className="tw-text-center tw-py-16 tw-text-[#999]">Failed to load analytics data.</div>;
    }

    var dailyUsers = data.dailyUsers || [];
    var dailyOrders = data.dailyOrders || [];
    var weeklyUsers = data.weeklyUsers || [];
    var disputeBySeller = data.disputeBySeller || [];

    return (
      <div>
        {/* KPI Cards */}
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4 tw-mb-6">
          <KpiCard icon="fa-shopping-cart" label="Avg Order Value" value={'$' + formatNum(data.aov)} color="#D4A843" />
          <KpiCard icon="fa-percent" label="Take Rate" value={data.takeRate + '%'} color="#10B981" />
          <KpiCard icon="fa-exclamation-triangle" label="Dispute Rate" value={data.disputeRate + '%'} color="#EF4444" />
          <KpiCard icon="fa-truck" label="Avg Ship Time" value={data.avgShipTimeHours < 24 ? (data.avgShipTimeHours + 'h') : (Math.round(data.avgShipTimeHours / 24) + 'd')} color="#3B82F6" />
          <KpiCard icon="fa-user-plus" label="New Users (7d)" value={data.newUsersThisWeek} color="#8B5CF6" />
          <KpiCard icon="fa-store" label="Active Sellers" value={data.activeSellers} color="#F59E0B" />
        </div>

        {/* Daily Orders Chart */}
        <div className="tw-rounded-xl tw-p-5 tw-mb-6" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Daily Orders (Last 30 Days)</h3>
          {dailyOrders.length > 0 ? (
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={dailyOrders} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ordersGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return v.slice(5); }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: '#999' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                    itemStyle={{ color: '#fff' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={function(v, name) { return [name === 'revenue' ? '$' + Number(v).toFixed(2) : v, name === 'revenue' ? 'Revenue' : 'Orders']; }}
                    labelFormatter={function(l) { return l; }}
                  />
                  <Area type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2.5} fill="url(#ordersGrad)" dot={false} activeDot={{ r: 5, fill: '#3B82F6' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="tw-text-center tw-py-8 tw-text-[#999]">No order data yet</div>
          )}
        </div>

        {/* Row: New Users Chart + Weekly Users + Dispute by Seller */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          {/* Daily New Users */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">New Users (Last 30 Days)</h3>
            {dailyUsers.length > 0 ? (
              <div style={{ width: '100%', height: 180 }}>
                <ResponsiveContainer>
                  <BarChart data={dailyUsers} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#999' }} tickFormatter={function(v) { return v.slice(8); }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                    <Bar dataKey="count" name="New Users" radius={[4, 4, 0, 0]}>
                      {dailyUsers.map(function(entry, index) {
                        return <Cell key={'du-' + index} fill={CHART_COLORS[index % CHART_COLORS.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data yet</div>
            )}
          </div>

          {/* Weekly Users */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Weekly Signups (Last 8 Weeks)</h3>
            {weeklyUsers.length > 0 ? (
              <div style={{ width: '100%', height: 180 }}>
                <ResponsiveContainer>
                  <BarChart data={weeklyUsers} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                    <XAxis dataKey="_id" tick={{ fontSize: 10, fill: '#999' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                    <Bar dataKey="count" name="Signups" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data yet</div>
            )}
          </div>

          {/* Top Dispute Sellers */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Top Disputed Sellers</h3>
            {disputeBySeller.length > 0 ? (
              <div className="tw-space-y-0">
                {disputeBySeller.map(function(s) {
                  return <DisputeSellerRow key={s._id} seller={s} onOpenUserModal={self.props.onOpenUserModal} />;
                })}
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No disputes yet</div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default AnalyticsOverview;
